import { Locale } from '../app/locale/resolveLocale';
import { Dimension, ScoreVector, dimensions, neutralScoreVector } from './dimensions';
import { PersonalityType } from './taxonomy';

type Localized = Record<Locale, string>;

type SurvivalKit = {
  weapon: Localized;
  defense: Localized;
  risk: Localized;
  repair: Localized;
};

export type ResultExperience = {
  punchline: string;
  profileVariants: string[];
  tags: string[];
  gestureFingerprint: Array<{ label: string; value: number }>;
  highRiskScenes: string[];
  triggerLine: string;
  survivalKit: Array<{ label: string; value: string }>;
};

const punchlines: Record<string, Localized> = {
  joke_shield_deflector: {
    'zh-CN': '我不是逃避，我是在加载表情包。',
    en: 'Not avoiding it. Loading the meme shield.'
  },
  clear_boundary_calm_repairer: {
    'zh-CN': '我可以温和，但边界线会发光。',
    en: 'Soft voice. Bright boundary.'
  },
  hidden_screenshot_archivist: {
    'zh-CN': '我不是八卦，我是在做时间线管理。',
    en: 'Not gossiping. Building the timeline.'
  },
  hidden_read_receipt_ghost: {
    'zh-CN': '已读不是结束，是潜水的开始。',
    en: 'Seen is not the end. It is the drift.'
  },
  hidden_group_chat_lawyer: {
    'zh-CN': '群聊可以乱，证据链不能断。',
    en: 'The chat may spiral. The receipts stay organized.'
  },
  hidden_meme_overlord: {
    'zh-CN': '梗不是逃生门，是统治区。',
    en: 'Not hiding behind memes. Running the meme system.'
  },
  hidden_midnight_replay: {
    'zh-CN': '白天装没事，半夜开复盘。',
    en: 'Fine by day. Replaying by midnight.'
  },
  hidden_plot_twister: {
    'zh-CN': '不一定制造混乱，但很会打开支线。',
    en: 'Not chaos for harm. Just every side quest unlocked.'
  }
};

const dimensionCopy: Record<Dimension, {
  tag: Localized;
  scene: Localized;
  trigger: Localized;
  kit: SurvivalKit;
}> = {
  directness: {
    tag: { 'zh-CN': '#直球', en: '#direct' },
    scene: { 'zh-CN': '当场说清', en: 'Direct Callout' },
    trigger: { 'zh-CN': '话到嘴边，直球自动上线。', en: 'Direct mode turns on when the room gets vague.' },
    kit: {
      weapon: { 'zh-CN': '直球', en: 'Direct ask' },
      defense: { 'zh-CN': '说清楚', en: 'Say it' },
      risk: { 'zh-CN': '太硬', en: 'Too blunt' },
      repair: { 'zh-CN': '补温度', en: 'Add warmth' }
    }
  },
  avoidance: {
    tag: { 'zh-CN': '#先撤', en: '#escape' },
    scene: { 'zh-CN': '已读不回', en: 'Read Receipt' },
    trigger: { 'zh-CN': '气氛一冷，撤退键先亮。', en: 'The escape key lights up when the mood freezes.' },
    kit: {
      weapon: { 'zh-CN': '隐身', en: 'Quiet exit' },
      defense: { 'zh-CN': '缓一缓', en: 'Take space' },
      risk: { 'zh-CN': '断联感', en: 'Looks gone' },
      repair: { 'zh-CN': '回来讲', en: 'Return clear' }
    }
  },
  conflictHeat: {
    tag: { 'zh-CN': '#开麦', en: '#heat' },
    scene: { 'zh-CN': '群聊升温', en: 'Group Heat' },
    trigger: { 'zh-CN': '一有不公，麦克风自己打开。', en: 'The mic opens itself when something feels unfair.' },
    kit: {
      weapon: { 'zh-CN': '开麦', en: 'Open mic' },
      defense: { 'zh-CN': '顶回去', en: 'Push back' },
      risk: { 'zh-CN': '上头', en: 'Runs hot' },
      repair: { 'zh-CN': '降一句', en: 'Cool line' }
    }
  },
  socialControl: {
    tag: { 'zh-CN': '#控场', en: '#control' },
    scene: { 'zh-CN': '群聊控温', en: 'Chat Control' },
    trigger: { 'zh-CN': '场面要乱时，控温系统启动。', en: 'The control deck powers up when the room tilts.' },
    kit: {
      weapon: { 'zh-CN': '场控', en: 'Vibe deck' },
      defense: { 'zh-CN': '转话题', en: 'Redirect' },
      risk: { 'zh-CN': '太会算', en: 'Overplays' },
      repair: { 'zh-CN': '给台阶', en: 'Offer ramp' }
    }
  },
  emotionalVolatility: {
    tag: { 'zh-CN': '#戏多', en: '#feelings' },
    scene: { 'zh-CN': '情绪爆点', en: 'Feeling Spike' },
    trigger: { 'zh-CN': '情绪不是小窗，是全屏弹出。', en: 'Feelings do not pop up small; they go fullscreen.' },
    kit: {
      weapon: { 'zh-CN': '真情绪', en: 'Real feels' },
      defense: { 'zh-CN': '先表达', en: 'Name it' },
      risk: { 'zh-CN': '过载', en: 'Overload' },
      repair: { 'zh-CN': '等冷却', en: 'Cool down' }
    }
  },
  repairIntent: {
    tag: { 'zh-CN': '#修复', en: '#repair' },
    scene: { 'zh-CN': '关系补丁', en: 'Repair Moment' },
    trigger: { 'zh-CN': '关系一裂，补丁包马上下载。', en: 'A repair patch downloads when the thread cracks.' },
    kit: {
      weapon: { 'zh-CN': '补丁', en: 'Patch kit' },
      defense: { 'zh-CN': '先解释', en: 'Clarify' },
      risk: { 'zh-CN': '写太多', en: 'Too much' },
      repair: { 'zh-CN': '一句准', en: 'One line' }
    }
  },
  boundaryFirmness: {
    tag: { 'zh-CN': '#边界', en: '#boundary' },
    scene: { 'zh-CN': '边界不适', en: 'Boundary Hit' },
    trigger: { 'zh-CN': '越线一出现，边界光墙升起。', en: 'The boundary wall rises when a line gets crossed.' },
    kit: {
      weapon: { 'zh-CN': '边界线', en: 'Boundary' },
      defense: { 'zh-CN': '拒绝术', en: 'Say no' },
      risk: { 'zh-CN': '太冷', en: 'Feels cold' },
      repair: { 'zh-CN': '给理由', en: 'Add why' }
    }
  },
  memeDefense: {
    tag: { 'zh-CN': '#发梗', en: '#meme' },
    scene: { 'zh-CN': '尴尬回合', en: 'Awkward Reply' },
    trigger: { 'zh-CN': '尴尬靠近时，表情包先举盾。', en: 'The meme shield goes up when awkwardness approaches.' },
    kit: {
      weapon: { 'zh-CN': '表情包', en: 'Memes' },
      defense: { 'zh-CN': '装没事', en: 'Play cool' },
      risk: { 'zh-CN': '慢认真', en: 'Delayed real' },
      repair: { 'zh-CN': '梗后讲', en: 'Then clarify' }
    }
  },
  impulsiveness: {
    tag: { 'zh-CN': '#秒反应', en: '#quick' },
    scene: { 'zh-CN': '临场反射', en: 'Quick Reflex' },
    trigger: { 'zh-CN': '还没想完，手已经替你投票。', en: 'The hand votes before the brain finishes loading.' },
    kit: {
      weapon: { 'zh-CN': '快手', en: 'Fast tap' },
      defense: { 'zh-CN': '先动', en: 'Move first' },
      risk: { 'zh-CN': '后悔快', en: 'Regret fast' },
      repair: { 'zh-CN': '撤回补', en: 'Undo well' }
    }
  },
  chaosAppetite: {
    tag: { 'zh-CN': '#看戏', en: '#chaos' },
    scene: { 'zh-CN': '剧情分支', en: 'Plot Branch' },
    trigger: { 'zh-CN': '剧情能分岔时，你会看到按钮发光。', en: 'Every side quest button glows when the story can branch.' },
    kit: {
      weapon: { 'zh-CN': '支线', en: 'Side quest' },
      defense: { 'zh-CN': '加一笔', en: 'Add twist' },
      risk: { 'zh-CN': '戏过量', en: 'Too spicy' },
      repair: { 'zh-CN': '拉回来', en: 'Bring back' }
    }
  }
};

const hiddenTags: Record<string, Localized[]> = {
  hidden_screenshot_archivist: [
    { 'zh-CN': '#截图', en: '#receipts' },
    { 'zh-CN': '#时间线', en: '#timeline' },
    { 'zh-CN': '#旁观', en: '#observer' }
  ],
  hidden_read_receipt_ghost: [
    { 'zh-CN': '#已读', en: '#seen' },
    { 'zh-CN': '#潜水', en: '#drift' },
    { 'zh-CN': '#慢回', en: '#later' }
  ],
  hidden_group_chat_lawyer: [
    { 'zh-CN': '#证据链', en: '#receipts' },
    { 'zh-CN': '#规则', en: '#rules' },
    { 'zh-CN': '#群聊', en: '#chat' }
  ],
  hidden_meme_overlord: [
    { 'zh-CN': '#梗王', en: '#meme' },
    { 'zh-CN': '#贴纸阵', en: '#stickers' },
    { 'zh-CN': '#控场', en: '#control' }
  ],
  hidden_midnight_replay: [
    { 'zh-CN': '#复盘', en: '#replay' },
    { 'zh-CN': '#半夜', en: '#midnight' },
    { 'zh-CN': '#补救', en: '#repair' }
  ],
  hidden_plot_twister: [
    { 'zh-CN': '#剧情手', en: '#plot' },
    { 'zh-CN': '#支线', en: '#sidequest' },
    { 'zh-CN': '#看戏', en: '#chaos' }
  ]
};

const hiddenTriggerLines: Record<string, Localized> = {
  hidden_screenshot_archivist: {
    'zh-CN': '别人还在吵，你已经把时间线和证据袋整理好了。',
    en: 'When the room gets noisy, the timeline and receipts are already sorted.'
  },
  hidden_read_receipt_ghost: {
    'zh-CN': '已读以后先潜水，等情绪退潮再决定要不要浮上来。',
    en: 'After seen, you drift below the surface until the emotional tide drops.'
  },
  hidden_group_chat_lawyer: {
    'zh-CN': '群聊开始跑偏时，你会自动调出规则、截图和前情提要。',
    en: 'When the chat bends reality, you bring rules, receipts, and context back.'
  },
  hidden_meme_overlord: {
    'zh-CN': '尴尬不是问题，贴纸阵一开，气氛就归你调度。',
    en: 'Awkwardness is manageable once the sticker matrix is under your control.'
  },
  hidden_midnight_replay: {
    'zh-CN': '白天看似翻篇，半夜自动打开关系复盘播放器。',
    en: 'The day moves on, then the relationship replay starts after midnight.'
  },
  hidden_plot_twister: {
    'zh-CN': '剧情能分岔时，你会看到支线按钮开始发光。',
    en: 'Every side quest button glows when the story can branch.'
  }
};

function scoreSource(type: PersonalityType, scores?: ScoreVector): ScoreVector {
  if (scores) return scores;
  if ('vector' in type) return type.vector;
  return neutralScoreVector;
}

function topDimensions(scores: ScoreVector) {
  return [...dimensions]
    .map((dimension) => ({
      dimension,
      value: scores[dimension],
      signal: Math.abs(scores[dimension] - 50)
    }))
    .sort((a, b) => b.signal - a.signal || b.value - a.value);
}

function gestureFingerprint(scores: ScoreVector, locale: Locale) {
  const labels: Record<Locale, string[]> = {
    'zh-CN': ['上滑逃避', '下滑开麦', '左滑装没事', '右滑修复', '长按忍住', '双击发梗'],
    en: ['Escape', 'Call Out', 'Play Cool', 'Repair', 'Hold In', 'Meme']
  };
  const values = [
    scores.avoidance,
    Math.round((scores.directness + scores.conflictHeat) / 2),
    scores.socialControl,
    scores.repairIntent,
    Math.round((scores.avoidance + scores.socialControl) / 2),
    scores.memeDefense
  ];

  return labels[locale].map((label, index) => ({ label, value: values[index] }));
}

function profileVariantsFor(
  type: PersonalityType,
  locale: Locale,
  primaryDimension: Dimension,
  strongest: Dimension[],
  triggerLine: string
) {
  const displayName = locale === 'zh-CN' ? type.zhShare : type.enShare;
  const detailName = locale === 'zh-CN' ? type.zhDetail : type.enDetail;
  const tags = strongest.map((dimension) => dimensionCopy[dimension].tag[locale]).join(locale === 'zh-CN' ? '、' : ', ');
  const scene = dimensionCopy[primaryDimension].scene[locale];
  const kit = dimensionCopy[primaryDimension].kit;

  if ('core' in type) {
    if (locale === 'zh-CN') {
      return [
        `核心模式｜「${displayName}」说的是${detailName}：${type.core}`,
        `触发时刻｜碰到${scene}，你通常先拿出${kit.weapon[locale]}稳住局面，再判断要不要把话讲深。`,
        `使用提醒｜这套模式会在${tags}之间切换。它能帮你缓冲压力，但最好给对方一个看得懂的真实态度。`
      ];
    }

    return [
      `Core pattern | ${displayName} points to ${detailName}: ${type.enDetail} is the style underneath the joke label.`,
      `Trigger moment | In a ${scene} moment, you tend to reach for ${kit.weapon[locale]} before deciding how much to say.`,
      `Use note | This pattern moves through ${tags}. It helps you manage pressure, but it works best when your real stance is still visible.`
    ];
  }

  if (locale === 'zh-CN') {
    return [
      `隐藏支线｜「${displayName}」不是随机彩蛋，而是${detailName}反复出现后的集中表现：${triggerLine}`,
      `触发时刻｜当类似反应连续出现，系统会把它从普通结果里拎出来，标成「${displayName}」。`,
      `使用提醒｜别人看到的是${displayName}这个梗名，真正的运行逻辑更接近${detailName}，并且常在${tags}里来回切换。`
    ];
  }

  return [
    `Hidden path | ${displayName} is not a random badge. It gathers repeated ${detailName} signals: ${triggerLine}`,
    `Trigger moment | When the same reaction keeps showing up, the result steps out of the regular pool as ${displayName}.`,
    `Use note | People see the meme label first, but the operating theme stays close to ${detailName} and ${tags}.`
  ];
}

export function resultExperience(type: PersonalityType, locale: Locale, scores?: ScoreVector): ResultExperience {
  const activeScores = scoreSource(type, scores);
  const ranked = topDimensions(activeScores);
  const primaryDimension = ranked[0].dimension;
  const strongest = ranked.slice(0, 3).map((item) => item.dimension);
  const fallbackPunchline = 'core' in type
    ? locale === 'zh-CN'
      ? type.core
      : `${type.enDetail} mode: reacts fast first, then makes sense of it later.`
    : locale === 'zh-CN'
      ? type.zhDetail
      : type.enDetail;
  const hiddenTagSet = hiddenTags[type.typeId];
  const kit = dimensionCopy[primaryDimension].kit;
  const triggerLine = 'trigger' in type
    ? hiddenTriggerLines[type.typeId]?.[locale] ?? type.enDetail
    : dimensionCopy[primaryDimension].trigger[locale];

  return {
    punchline: punchlines[type.typeId]?.[locale] ?? fallbackPunchline,
    profileVariants: profileVariantsFor(type, locale, primaryDimension, strongest, triggerLine),
    tags: hiddenTagSet?.map((item) => item[locale]) ?? strongest.map((dimension) => dimensionCopy[dimension].tag[locale]),
    gestureFingerprint: gestureFingerprint(activeScores, locale),
    highRiskScenes: strongest.map((dimension) => dimensionCopy[dimension].scene[locale]),
    triggerLine,
    survivalKit: [
      { label: locale === 'zh-CN' ? '随身武器' : 'Weapon', value: kit.weapon[locale] },
      { label: locale === 'zh-CN' ? '防御姿势' : 'Defense', value: kit.defense[locale] },
      { label: locale === 'zh-CN' ? '翻车风险' : 'Risk', value: kit.risk[locale] },
      { label: locale === 'zh-CN' ? '补救方式' : 'Repair', value: kit.repair[locale] }
    ]
  };
}
